import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BackLink, PageHeader } from "@/components/ui/page";
import { Checkbox, Field, Form, Input, Textarea } from "@/components/ui/field";
import { api, errorMessage, ifMatch } from "@/services/api";
import { AddButton, ListSection } from "@/components/ui/list-section";
import { Skeleton, SkeletonLines } from "@/components/ui/skeleton";
import { queryKeys } from "@/services/queries";
import { useRecordParam } from "@/hooks/use-record-param";
import { PHONE, useMediaQuery } from "@/hooks/use-media-query";
import type { Translate, TranslationKey } from "@/constants/i18n";
import { deleteResource, listAll, listResource } from "@/utils/geo";
import { NAV_AREAS, PAGE_LABEL_OF_MODEL } from "@/constants/nav-permissions";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";
import { ConfirmationModal } from "./ConfirmationModal";

/**
 * Roles are groups. A role is a name and a list of things its holders may do;
 * the editor shows that list as a grid of plain verbs per kind of record, so
 * nobody has to know what `change__access_policy_setting` means.
 */

interface GroupSummary {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
  /**
   * The role that holds the whole catalogue by definition.
   *
   * Sent by the API rather than worked out from the name here: the server
   * refuses a permission change on it and reports anybody holding it as a
   * superuser in `/me`, and a name comparison in the browser would be the
   * same rule written a third time.
   *
   * Optional only for a server too old to send it. Absent reads as false,
   * which leaves the grid editable — the save is still refused, so the worst
   * case is a refusal instead of a fixed tick.
   */
  is_superuser?: boolean;
  /**
   * Sent back as `If-Match` on save and on delete; both routes answer 428
   * without it. It belongs on the summary because the list row is where Delete
   * is pressed, and that row has to carry the timestamp its own request needs.
   */
  updated_at?: string;
}

interface GroupRead extends GroupSummary {
  permissions: { id: number; codename: string; name: string }[];
}

interface Permission {
  id: number;
  codename: string;
  name: string;
  content_type: { id: number; model: string; verbose_name: string };
}

/**
 * The four verbs every kind of record has, in the words a person would use —
 * and a fifth column that is not a verb at all, and takes away rather than
 * gives.
 *
 * `hide` asks whether this screen belongs in the person's interface, which is
 * a different question from whether they may read the records, and it is the
 * only column here that no route enforces. It exists because the two used to
 * be one answer: a role that needed clubs in a dropdown somewhere got Business
 * Unit in its sidebar as well, and there was no way to say otherwise. Tick it
 * and the row leaves the menu and its address stops opening, while every list
 * and picker fed by `view__club` goes on working.
 *
 * Ticked nowhere by default, so a role behaves exactly as it did before anyone
 * knew this column was here. It never opens anything either: the interface
 * asks for the View tick *and* the absence of this one, so a screen they may
 * not read stays gone whether or not it is ticked.
 *
 * Only the screens somebody navigates to have one, so the column is empty on
 * the rows that are parts of a page — a member's notes, a ticket's comments —
 * and on the tables nobody visits directly.
 *
 * `view_own` is the sixth, and restricts in the same way: ticked, the person
 * is shown the rows they created and no others — not in the list, not in the
 * total, and not by typing an id into the address bar. It narrows and never
 * widens, so ticking it for somebody who cannot read the records at all
 * changes nothing. Only the kinds of record people spend the day creating have
 * one: a province and a promo code are lists everybody chooses from, and
 * narrowing those to their author would empty the pickers that read them.
 */
const VERBS = [
  { action: "view", label: "roles.view", hint: "roles.viewHint" },
  { action: "add", label: "roles.add", hint: "roles.addHint" },
  { action: "change", label: "roles.change", hint: "roles.changeHint" },
  { action: "delete", label: "roles.remove", hint: "roles.removeHint" },
  { action: "hide", label: "roles.hide", hint: "roles.hideHint" },
  { action: "view_own", label: "roles.own", hint: "roles.ownHint" },
] as const satisfies readonly { action: string; label: TranslationKey; hint: TranslationKey }[];

const titleCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Whether holding this codename takes something away rather than granting it.
 *
 * The same list the server keeps in `catalog.RESTRICTING_ACTIONS`, for the two
 * places this side has to agree with it: "Can do everything" must not sweep
 * them in, and the count beside the heading must not measure a role against a
 * total it was never meant to reach.
 */
const restricts = (codename: string) =>
  codename.startsWith("hide__") || codename.startsWith("view_own__");

/** Plumbing nobody grants by hand; keeping it out of the grid keeps the grid honest. */
export const HIDDEN_MODELS = new Set([
  "refresh_token",
  "password_reset_token",
  "content_type",
  "permission",
  // One row, created once, from the top of the access-policy screen — which is
  // also the only thing that reads or writes it. It is not a section of
  // Configuration and nobody goes looking for it, so listing it only invited
  // the question the screenshot asked. The policy screens' own codenames open
  // `/organizations`, so the bootstrap step is still reachable without it.
  "organization",
]);

/** Where the product's word differs from the model's. */
const LABELS: Record<string, TranslationKey> = {
  group: "roles.labelGroup",
  user: "roles.labelUser",
  member: "roles.model.member",
  member_note: "roles.model.memberNote",
  member_attachment: "roles.model.memberAttachment",
  member_log: "roles.model.memberLog",
  member_saved_filter: "roles.model.memberSavedFilter",
  member_card_history: "roles.model.cardHistory",
  member_card_transaction: "roles.model.cardTransaction",
  member_print_history: "roles.model.printHistory",
  member_acquisition_detail: "roles.model.acquisitionDetail",
  member_amendment_detail: "roles.model.amendmentDetail",
  member_supplementary_history: "roles.model.supplementaryHistory",
  non_member: "roles.model.nonMember",
  idle_time: "cfg.idleTitle",
  password_expiry: "cfg.passwordExpiry",
  employee_activity_log: "roles.model.employeeActivityLog",
  organization: "roles.model.organization",
  province: "roles.model.province",
  city: "roles.model.city",
  barangay: "roles.model.barangay",
  club: "roles.model.club",
  yellow_page: "roles.model.yellowPage",
  yellow_page_category: "roles.model.yellowPageCategory",
  promo_code: "roles.model.promoCode",
  reason: "roles.model.reason",
  business_type: "roles.model.businessType",
  // Screens rather than kinds of record — see the backend catalogue's
  // SCREEN_PERMISSIONS. They carry one verb, View, and the grid says so.
  member_analytics: "nav.memberAnalytics",
};

/**
 * Which section of the table a model belongs to: the sidebar area it is
 * administered from.
 *
 * Thirty models as one flat list is a wall nobody reads to the end of, and the
 * four hand-made groups this replaces ("Catalogue & settings") were a second
 * vocabulary to learn beside the menu. Now a section is a place: someone asked
 * to give a person the Yellow Page opens the Yellow Page section, because that
 * is what the sidebar calls it too. `nav-permissions.ts` is the one list; the
 * menu, the router and this grid all read it, so a model moves once.
 *
 * There is no catch-all. A section headed "Other records" with a table name
 * under it answers a question nobody asked, and it only ever held models the
 * map had not placed yet. A model the map does not name is simply not drawn —
 * and `nav-permissions.test.ts` fails if the catalogue ever contains one, which
 * is the same guarantee caught at build time instead of shown to an
 * administrator at runtime.
 */
const GROUPS: { key: TranslationKey; sectionKey: string; models: string[] }[] =
  NAV_AREAS.filter((area) => area.models.length > 0).map((area) => ({
    key: area.label,
    sectionKey: area.key,
    models: [...area.models],
  }));

/** The sidebar areas that keep no records of their own, so have no section. */
const BORROWED = NAV_AREAS.filter((area) => area.models.length === 0 && area.perms.length > 0);

/**
 * One verb, as a line of its own: the word on the left, the box on the right.
 *
 * The grid's columns, unfolded — and unfolded downwards rather than across. Six
 * boxes wrapped into a paragraph is what the first attempt at this did, and
 * three levels of them (every module, a section, a record) all looked the same:
 * a wall of ticks with no way to tell which of the three you were reading. A
 * row reads as a setting, a stack of rows reads as a list of settings, and the
 * heading above them says what they are settings *of*.
 *
 * `aria-label` is the same sentence the table's box carries — "View — Members"
 * — so a screen reader hears one thing whatever the screen is doing, and the
 * visible word is what a pointer reads instead of a column header six hundred
 * pixels above it.
 */
function VerbRow({
  word,
  name,
  column,
  disabled,
  has,
  part,
  toggle,
}: {
  word: string;
  name: string;
  /** Every codename this one row governs. Empty means the record has no such verb. */
  column: readonly string[];
  disabled: boolean;
  has: (codenames: string[]) => boolean;
  part: (codenames: string[]) => boolean;
  toggle: (codenames: string[], on: boolean) => void;
}) {
  // Drawn only where it means something. The table writes an em-dash in the
  // cell because a column has to be filled; a list has no hole to leave, and
  // six rows of which two do nothing is a worse answer than four.
  if (column.length === 0) return null;
  return (
    <label className="hover:bg-surface-sunken -mx-2 flex cursor-pointer items-center justify-between gap-4 rounded-lg px-2 py-2.5 transition-colors">
      <span className="text-fg text-callout min-w-0">{word}</span>
      <Checkbox
        disabled={disabled}
        aria-label={name}
        checked={has([...column])}
        ref={(box) => {
          if (box) box.indeterminate = part([...column]);
        }}
        onChange={(event) => toggle([...column], event.target.checked)}
      />
    </label>
  );
}

/** The key standing for the permissions that apply to every module at once. */
const EVERY_MODULE = "\u0000every";

interface GridRow {
  model: string;
  label: string;
  verbs: (Permission | null)[];
  columns: string[][];
  options: Permission[];
}

interface GridSection {
  key: string;
  label: TranslationKey;
  rows: GridRow[];
}

/**
 * The permission grid on a phone: a list of modules, and one module at a time.
 *
 * The table is the right shape for a mouse and a wide screen — thirty rows
 * against six columns, every answer visible at once — and the wrong shape for
 * anything narrower than it. Rather than squeeze it, this asks the same
 * questions one screenful at a time: which module, then which verbs.
 */
function PhoneGrid({
  sections,
  rows,
  opened,
  setOpened,
  tally,
  disabled,
  selected,
  has,
  part,
  toggle,
  t,
}: {
  sections: GridSection[];
  rows: GridRow[];
  opened: string | null;
  setOpened: (key: string | null) => void;
  tally: (codenames: readonly string[]) => { granted: number; total: number };
  disabled: boolean;
  selected: Set<string>;
  has: (codenames: string[]) => boolean;
  part: (codenames: string[]) => boolean;
  toggle: (codenames: string[], on: boolean) => void;
  t: Translate;
}) {
  /** The codenames one module governs — its records' columns, options included. */
  const codenamesOf = (section: GridSection) =>
    section.rows.flatMap((row) => row.columns.flat());

  /** The plain verb of every record, which is what "for every module" means. */
  const everyModule = VERBS.map((verb, index) =>
    rows
      .map((row) => row.verbs[index]?.codename)
      .filter((code): code is string => Boolean(code)),
  );

  const section = sections.find((entry) => entry.key === opened);
  // A module that is no longer there — the catalogue reloaded and lost it — is
  // the list again, derived rather than corrected: setting state from a render
  // would be this component writing to its parent's while that parent renders.
  if (opened === null || (opened !== EVERY_MODULE && !section)) {
    return (
      <ul className="divide-line divide-y">
        {/* The table's header row, which ticks a verb across everything. Kept
            first and named for what it does, rather than sitting unlabelled at
            the top of a list of modules looking like one of them. */}
        <li>
          <button
            type="button"
            onClick={() => setOpened(EVERY_MODULE)}
            className="hover:bg-surface-sunken flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors"
          >
            <span className="text-fg text-callout min-w-0 flex-1 font-medium">
              {t("roles.everyModule")}
            </span>
            <Tally {...tally(everyModule.flat())} />
            <ChevronRight className="text-fg-subtle size-4 shrink-0" aria-hidden />
          </button>
        </li>

        {sections.map((section) => (
          <li key={section.key}>
            <button
              type="button"
              onClick={() => setOpened(section.key)}
              className="hover:bg-surface-sunken flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors"
            >
              {/* The name and one number. A count of the records inside sat
                  here too for a while, and two numbers on one row is the thing
                  that makes a list of modules read as a puzzle: the only count
                  worth showing is how much of the module this role holds. */}
              <span className="text-fg text-callout min-w-0 flex-1 font-medium">
                {t(section.label)}
              </span>
              <Tally {...tally(codenamesOf(section))} />
              <ChevronRight className="text-fg-subtle size-4 shrink-0" aria-hidden />
            </button>
          </li>
        ))}

        {rows.length === 0 ? (
          <li className="text-fg-muted px-4 py-10 text-center">{t("roles.noPermissions")}</li>
        ) : null}
      </ul>
    );
  }

  const heading = section ? t(section.label) : t("roles.everyModule");

  return (
    <div>
      {/* The way back, at the top of what it goes back from. Sticky under the
          Save bar, because a module's records run past a screenful and a back
          link at the top of a long list is a scroll away by the time it is
          wanted. */}
      <div className="bg-surface-raised hairline-b sticky top-[var(--role-bar)] z-10 flex items-center gap-2 px-2 py-2.5">
        <BackLink onClick={() => setOpened(null)}>{t("roles.allModules")}</BackLink>
        <span className="text-fg text-callout min-w-0 truncate font-medium">{heading}</span>
      </div>

      {section ? (
        <div className="divide-line divide-y">
          {/* Everything in this module, before the records it is made of: the
              table's section row, which is one tick where the rows below are
              many. */}
          <section className="px-4 py-3">
            <h4 className="text-fg-muted text-footnote mb-1 uppercase">
              {t("roles.wholeModule")}
            </h4>
            {VERBS.map((verb, index) => (
              <VerbRow
                key={verb.action}
                word={t(verb.label)}
                name={`${t(verb.label)} — ${heading}`}
                column={section.rows.flatMap((row) => row.columns[index] ?? [])}
                disabled={disabled}
                has={has}
                part={part}
                toggle={toggle}
              />
            ))}
          </section>

          {section.rows.map((row) => (
            <section key={row.model} className="px-4 py-3">
              <h4 className="text-fg text-callout mb-1 flex items-center justify-between gap-3 font-medium">
                {row.label}
                <Tally {...tally(row.columns.flat())} />
              </h4>
              {row.columns.map((column, index) => (
                <VerbRow
                  key={VERBS[index]?.action}
                  word={t(VERBS[index]!.label)}
                  name={`${t(VERBS[index]!.label)} — ${row.label}`}
                  column={column}
                  disabled={disabled}
                  has={has}
                  part={part}
                  toggle={toggle}
                />
              ))}
              {/* The named grants a column rolls several of into one — "enrol
                  an affiliate" under Members' Add. Indented, so they read as
                  parts of the verb above rather than verbs of their own. */}
              {row.options.length ? (
                <div className="border-line mt-2 ml-1 border-l pl-3">
                  {row.options.map((perm) => (
                    <label
                      key={perm.codename}
                      className="hover:bg-surface-sunken -mx-2 flex cursor-pointer items-center justify-between gap-4 rounded-lg px-2 py-2 transition-colors"
                    >
                      <span className="text-fg-muted text-subhead min-w-0">{perm.name}</span>
                      <Checkbox
                        disabled={disabled}
                        checked={selected.has(perm.codename)}
                        onChange={(event) => toggle([perm.codename], event.target.checked)}
                      />
                    </label>
                  ))}
                </div>
              ) : null}
            </section>
          ))}
        </div>
      ) : (
        <section className="px-4 py-3">
          <p className="text-fg-muted text-footnote mb-2">{t("roles.everyModuleHint")}</p>
          {VERBS.map((verb, index) => (
            <VerbRow
              key={verb.action}
              word={t(verb.label)}
              name={t("roles.forEvery").replace("{verb}", t(verb.label))}
              column={everyModule[index] ?? []}
              disabled={disabled}
              has={has}
              part={part}
              toggle={toggle}
            />
          ))}
        </section>
      )}
    </div>
  );
}

/** "3 of 24" — how much of a section or a record this role holds. */
function Tally({ granted, total }: { granted: number; total: number }) {
  return (
    <span className="text-fg-muted text-footnote shrink-0 tabular-nums">
      {granted}/{total}
    </span>
  );
}

/** `view__club` as "View Business Unit" — the grid's words, not the API's. */
function labelForCodename(codename: string, catalogue: Permission[], t: Translate): string {
  const [action, model] = codename.split("__");
  if (!model) return codename;
  const verb = VERBS.find((entry) => entry.action === action);
  // The catalogue is the authority on which model a codename belongs to, but
  // the codename carries it too, so an unlisted permission still reads plainly.
  const known = catalogue.find((p) => p.codename === codename)?.content_type.model ?? model;
  const label = PAGE_LABEL_OF_MODEL.get(known) ?? LABELS[known];
  const record = label ? t(label) : known.replace(/_/g, " ");
  return verb ? `${t(verb.label)} ${record}` : record;
}

const PAGE = 10;

export function RolesSection() {
  const t = useTranslation();

  // Track the active group selected for deletion
  const [deletingGroup, setDeletingGroup] = useState<GroupSummary | null>(null);

  const queryClient = useQueryClient();

  const groups = useInfiniteQuery({
    queryKey: ["/groups", "infinite"],
    queryFn: ({ pageParam, signal }) =>
      listResource<GroupSummary>(
        "/groups",
        { page: pageParam, size: PAGE, sort: "id" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const remove = useMutation({
    mutationFn: (group: GroupSummary) => deleteResource("/groups", group.id, group.updated_at),
    onSuccess: (_result, group) => {
      toast.success(t("roles.deleted"));
      queryClient.removeQueries({ queryKey: ["/groups", group.id] });
      void queryClient.invalidateQueries({ queryKey: ["/groups"] });
      // Losing a role takes its permissions from everyone who was in it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
    onError: (error) => toast.error(errorMessage(error, t("roles.deleteFailed"))),
  });

  const rows = groups.data?.pages.flatMap((page) => page.items) ?? [];
  const { editing, setEditing, loading } = useRecordParam<GroupSummary>(
    "edit",
    "role",
    "/groups",
  );

  // The role is read when it is opened; the same skeleton the editor shows
  // while it reads its own group, so the two waits are one.
  if (loading) {
    return (
      <div className="grid gap-6 py-6">
        <Skeleton className="h-9 w-72 rounded-lg" />
        <SkeletonLines rows={6} />
      </div>
    );
  }

  if (editing !== null) {
    return (
      <RoleEditor
        groupId={editing === "new" ? null : editing.id}
        onDone={() => {
          setEditing(null);
          // The list is on screen, so invalidating refetches it. The role's own
          // record is not: invalidating an inactive query only marks it stale,
          // and the next open would read that stale copy out of the cache,
          // render the form from it, and seed the tickboxes with what the role
          // held *before*. Dropping it means the next open has nothing to read
          // and waits for the server, which is the state it should show.
          queryClient.removeQueries({
            queryKey: ["/groups", editing === "new" ? null : editing.id],
          });
          void queryClient.invalidateQueries({ queryKey: ["/groups"] });
        }}
        onSaved={(group) => {
          // Stay put, with the record the server just handed back — the new
          // `updated_at` most of all, since the next save sends it as `If-Match`.
          queryClient.setQueryData(["/groups", group.id], group);
          void queryClient.invalidateQueries({ queryKey: ["/groups"] });
          // A role's permissions are the signed-in person's own the moment they
          // are in it, and `/me` is cached for a minute with nothing to clear
          // it. Editing your own role and finding the menu, the Configuration
          // rail and every disabled box still answering to what you held before
          // — until a reload — is that minute.
          void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
          // A role that has just been created is no longer new: bind the form to
          // it, or the next Save would make a second one.
          if (editing === "new") setEditing(group);
        }}
      />
    );
  }

  return (
    <>
      {/* A page's own title now, not a section's. This was the right-hand
          column of the Configuration rail, which had a page heading above it
          already and a gutter of its own; standing on its own the screen owes
          the reader both. */}
      <PageHeader title={t("nav.roleConfiguration")} description={t("roles.intro")} />

      <ListSection
        title={t("cfg.roles")}
        shown={rows.length}
        total={groups.data?.pages[0]?.total}
        refreshKeys={[["/groups"]]}
        action={<AddButton onClick={() => setEditing("new")}>{t("roles.addRole")}</AddButton>}
        hasMore={groups.hasNextPage}
        loadingMore={groups.isFetchingNextPage}
        pending={groups.isPending}
        onLoadMore={() => void groups.fetchNextPage()}
        empty={t("roles.none")}
      >
        {rows.map((group) => (
          <li key={group.id} className="flex items-center justify-between gap-3 py-4 sm:gap-4">
            <span className="min-w-0">
              {/* Wrapping, not truncating: a role's name is the whole of what
                  the row is for, and "Membership supervisor (Nor…" on a phone
                  is a row that has stopped answering the question. The badge
                  travels with it and drops to the next line rather than
                  squeezing the name against the actions. */}
              <span className="text-fg text-callout flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
                {group.name}
                {group.is_system ? <Badge tone="neutral">{t("roles.builtIn")}</Badge> : null}
              </span>
              {group.description ? (
                <span className="text-fg-muted text-subhead mt-0.5 block truncate">
                  {group.description}
                </span>
              ) : null}
            </span>
            {/* Tighter on a phone, where five rems between two words is most of
                the width the name has left. */}
            <span className="text-callout flex shrink-0 items-center gap-2 sm:gap-5">
              <button
                type="button"
                onClick={() => setEditing(group)}
                className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 transition-colors"
              >
                {t("common.edit")}
              </button>
              {!group.is_system && (
                <button
                  type="button"
                  onClick={() => setDeletingGroup(group)}
                  className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 transition-colors"
                >
                  {t("common.delete")}
                </button>
              )}
            </span>
          </li>
        ))}
      </ListSection>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={deletingGroup !== null}
        onClose={() => setDeletingGroup(null)}
        onConfirm={() => {
          if (deletingGroup) {
            remove.mutate(deletingGroup);
          }
          setDeletingGroup(null);
        }}
        title={t("roles.confirmDeleteTitle")}
        message={
          deletingGroup ? t("roles.confirmDelete").replace("{name}", deletingGroup.name) : ""
        }
      />
    </>
  );
}

function RoleEditor({
  groupId,
  onDone,
  onSaved,
}: {
  groupId: number | null;
  onDone: () => void;
  onSaved: (group: GroupRead) => void;
}) {
  // The whole catalogue, however many pages: a role must be able to see every permission.
  const permissions = useQuery({
    queryKey: ["/permissions", "all"],
    queryFn: ({ signal }) => listAll<Permission>("/permissions", { sort: "codename" }, signal),
    staleTime: 5 * 60_000,
  });
  const group = useQuery({
    queryKey: ["/groups", groupId],
    queryFn: async ({ signal }) =>
      (await api.get<GroupRead>(`/groups/${groupId}`, { signal })).data,
    enabled: groupId !== null,
  });

  if (permissions.isPending || (groupId !== null && group.isPending)) {
    return (
      <div className="grid gap-6 py-6">
        <Skeleton className="h-9 w-72 rounded-lg" />
        <SkeletonLines rows={6} />
      </div>
    );
  }
  // Keyed so a different role gets a fresh form; state starts from the data.
  return (
    <RoleForm
      key={groupId ?? "new"}
      groupId={groupId}
      initial={group.data ?? null}
      permissions={(permissions.data ?? []).filter(
        (p) => !HIDDEN_MODELS.has(p.content_type.model),
      )}
      onDone={onDone}
      onSaved={onSaved}
    />
  );
}

function RoleForm({
  groupId,
  initial,
  permissions,
  onDone,
  onSaved,
}: {
  groupId: number | null;
  initial: GroupRead | null;
  permissions: Permission[];
  /** Leaving without saving: the back arrow and Cancel. */
  onDone: () => void;
  /** Saved, and staying put — with the record as the server now holds it. */
  onSaved: (group: GroupRead) => void;
}) {
  const t = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initial?.permissions.map((p) => p.codename) ?? []),
  );
  const [error, setError] = useState<string | null>(null);

  // Follow the record when the server moves it.
  //
  // These three start from `initial` and, until now, never looked at it again:
  // the form showed what had been typed into it for as long as it was open.
  // That was invisible while saving navigated back to the list, because the
  // form was gone a moment later. Saving keeps you here now, so the boxes have
  // to show what was *saved* — the answer the server sent back, not the
  // request. Anywhere the two differ, the old behaviour left the screen
  // quietly lying about what the role holds.
  //
  // Keyed on the version stamp, so this runs when the row actually changed and
  // not on every render. Written during render rather than in an effect, which
  // is the pattern the number boxes on the policy screen already use: no second
  // paint showing the stale value first.
  const [seenVersion, setSeenVersion] = useState(initial?.updated_at);
  if (initial !== null && initial.updated_at !== seenVersion) {
    setSeenVersion(initial.updated_at);
    setName(initial.name);
    setDescription(initial.description ?? "");
    setSelected(new Set(initial.permissions.map((permission) => permission.codename)));
  }

  /**
   * Whether anything here differs from the record as the server holds it.
   *
   * Save is disabled until something does. An enabled Save on an untouched form
   * invites a write that changes nothing — and every one of those still bumps
   * `updated_at`, so it would stale out anyone else with the role open, for a
   * save that was a misfire.
   *
   * A role that does not exist yet is always a change: there is nothing to
   * differ from, and the name is what the button waits for instead.
   */
  const dirty = useMemo(() => {
    if (initial === null) return true;
    const asSent = (text: string) => text.trim() || null;
    if (asSent(name) !== asSent(initial.name)) return true;
    if (asSent(description) !== asSent(initial.description ?? "")) return true;
    const held = initial.permissions.map((p) => p.codename);
    // Sets, so order is not a difference; sizes first, since it usually settles it.
    return held.length !== selected.size || held.some((code) => !selected.has(code));
  }, [initial, name, description, selected]);
  // The first section is open; the rest wait to be asked for. Opening all four
  // at once is the flat wall this replaces.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([GROUPS[0]!.sectionKey]));

  // Rows are kinds of record; each has the four verbs where they exist, and
  // any extra, hand-named permissions on their own.
  const rows = useMemo(() => {
    const byModel = new Map<string, { label: string; perms: Permission[] }>();
    for (const perm of permissions) {
      const key = perm.content_type.model;
      const entry = byModel.get(key) ?? {
        label:
          t(PAGE_LABEL_OF_MODEL.get(key) ?? LABELS[key] ?? ("" as TranslationKey)) ||
          titleCase(perm.content_type.verbose_name),
        perms: [],
      };
      entry.perms.push(perm);
      byModel.set(key, entry);
    }
    return [...byModel.entries()]
      .map(([model, { label, perms }]) => {
        const verbs = VERBS.map(
          (verb) => perms.find((p) => p.codename === `${verb.action}__${model}`) ?? null,
        );
        const extras = perms.filter(
          (p) => !VERBS.some((verb) => p.codename === `${verb.action}__${model}`),
        );
        // What each column governs: its own plain verb, plus every hand-named
        // permission that shares its action. Enrolling is four things on the
        // Members row — a member, an affiliate, an employee, a complimentary —
        // so Add there is the whole set rather than one of the four, and ticking
        // it grants them all.
        const columns = VERBS.map((verb, index) =>
          [
            verbs[index]?.codename,
            ...extras
              .filter((p) => p.codename.startsWith(`${verb.action}__`))
              .map((p) => p.codename),
          ].filter((code): code is string => Boolean(code)),
        );
        // A column that has grown into a roll-up can no longer set its own verb
        // on its own, so the verb joins the named options underneath — otherwise
        // "enrol an ordinary member" would be reachable only by granting the
        // other three with it.
        const rolledUp = VERBS.flatMap((_, index) =>
          columns[index]!.length > 1 && verbs[index] ? [verbs[index]] : [],
        );
        return {
          model,
          label,
          verbs,
          columns,
          options: [...rolledUp, ...extras].sort((a, b) =>
            a.codename.localeCompare(b.codename),
          ),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [permissions, t]);

  /** The same rows, folded into the sections of the table. */
  const sections = useMemo(
    () =>
      GROUPS.map((group) => ({
        key: group.sectionKey,
        label: group.key,
        rows: rows.filter((row) => group.models.includes(row.model)),
      })).filter((section) => section.rows.length > 0),
    [rows],
  );

  /**
   * Everything "Can do everything" means — which is every grant, and not the
   * hiding marks.
   *
   * `hide__x` is the one codename here whose presence takes something away, so
   * sweeping it in with the rest would give the most powerful role in the
   * product a blank sidebar. Somebody pressing this is saying "let them do
   * anything", never "and show them none of it". The column is still there to
   * tick by hand, one screen at a time, which is the only way anybody would
   * ever mean it.
   */
  const all = useMemo(
    () => permissions.map((p) => p.codename).filter((c) => !restricts(c)),
    [permissions],
  );
  const grantable = useMemo(() => new Set(all), [all]);
  const viewing = useMemo(() => all.filter((c) => c.startsWith("view__")), [all]);
  const toggle = (codenames: string[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const code of codenames)
        if (on) next.add(code);
        else next.delete(code);
      return next;
    });
  const has = (codenames: string[]) =>
    codenames.length > 0 && codenames.every((c) => selected.has(c));
  /** Some but not all — the dash the table's checkbox draws for a part-ticked page. */
  const part = (codenames: string[]) =>
    !has(codenames) && codenames.some((c) => selected.has(c));

  const save = useMutation({
    mutationFn: (body: { name: string; description: string | null; codenames: string[] }) =>
      groupId === null
        ? api.post<GroupRead>("/groups", body)
        : api.patch<GroupRead>(`/groups/${groupId}`, body, ifMatch(initial)),
    // Saving keeps you where you are. Being thrown back to the list after every
    // save meant reopening the role to carry on with it, and on a grid of a
    // hundred tickboxes that is most of the work. Both routes answer with the
    // record as saved, so the form can take the new state — the freshly stamped
    // `updated_at` above all, which the next save sends as `If-Match`; without
    // it a second save would be refused as stale.
    onSuccess: (response) => {
      toast.success(groupId === null ? t("roles.created") : t("roles.updated"));
      onSaved(response.data);
    },
    onError: (err) => setError(errorMessage(err, t("roles.saveFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate({
      name: name.trim(),
      description: description.trim() || null,
      codenames: [...selected],
    });
  };

  const isSystem = initial?.is_system ?? false;
  /**
   * The role that holds the whole catalogue by definition.
   *
   * Its grants are not a choice somebody made, so they are not a choice
   * somebody unmakes: the bootstrap tops this role up with everything on every
   * upgrade, so a permission unticked here would be back by the next deploy.
   * The boxes are drawn fixed rather than left to be pressed and then refused
   * — and the server refuses it too, for anything that is not this screen.
   *
   * The API says which role it is. Not the name, and not what the role
   * happens to hold: an earlier version of this asked "does it hold every
   * grantable codename?" and locked Manager / Corporate Admin, which holds all
   * of them by configuration. Being fully granted is a state somebody chose
   * and can unchoose; this is the one role whose grants are not a choice.
   */
  const holdsEverything = initial?.is_superuser ?? false;
  // Seven columns against a phone's width is fifty pixels a column, with the
  // one that says which record you are looking at the first to go. Below `sm`
  // the same grid is drawn as a list instead. Rendered rather than hidden with
  // a utility: one set of tickboxes is on the page at a time, so a label points
  // at one box and a screen reader reads the grid once.
  const phone = useMediaQuery(PHONE);
  /**
   * Which module's permissions are open on a phone — `EVERY_MODULE` for the
   * ones that apply across all of them, or `null` for the list of modules.
   *
   * A drill-down rather than a page of everything. The grid is thirty records
   * of six verbs; on a phone that is a hundred and eighty controls in one
   * scroll, and every attempt to flatten it reads as noise however it is laid
   * out. One list of modules, and one module at a time inside it, is the shape
   * a phone has for this — the same shape its own settings use.
   */
  const [opened, setOpened] = useState<string | null>(null);

  /** How much of a set of codenames this role holds, ignoring the marks that take away. */
  const tally = (codenames: readonly string[]) => {
    const unique = [...new Set(codenames)].filter((code) => grantable.has(code));
    return {
      granted: unique.filter((code) => selected.has(code)).length,
      total: unique.length,
    };
  };

  return (
    /* `--role-bar` is the height of the sticky Save bar, and so how far down
       the grid's own header has to pin: 32px of controls between `py-4`, which
       is 4rem. Below `sm` the bar wraps to two rows — title, then Save — and
       the taller value is what keeps the column headers from sliding underneath
       it. One declaration, read by the `<th>`s far below, because a number
       spelled twice is a number that will disagree with itself. */
    <Form onSubmit={submit} className="[--role-bar:6.75rem] sm:[--role-bar:4rem]">
      {/* Save travels with the title. The grid below runs to a hundred and
          eight tickboxes, and the button used to sit under the last of them:
          every save was a scroll to the bottom and a scroll back to carry on.
          Sticky inside the shell's one scroller, so it is wherever you are.
          Cancel is gone with it — it did what the back arrow already does, and
          two ways out either side of Save is a coin toss nobody needs. */}
      {/* No rule under it: the bar carries the page's own ground, and a line
          drawn across one grid column stops where the column does, which reads
          as a border with a piece missing rather than as a divider.

          `-mx-2 px-2` widens the ground without moving anything on it. The
          cards below carry `--shadow-lift`, which is a half-pixel ring on every
          side over a 2px drop — so a background only as wide as the column let
          the ring show past it, and scrolling dragged two slivers of card edge
          up either side of the bar. */}
      <div className="bg-canvas sticky top-0 z-20 -mx-2 px-2 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {/* Back is the arrow alone: beside a title it is the one control
                whose meaning the icon already carries, and the word next to it
                only widened the row. `type="button"` because it sits inside the
                form — without it the browser treats it as another submit. */}
            <BackLink onClick={onDone}>{t("roles.back")}</BackLink>
            <h2 className="text-title-2 text-heading truncate">
              {groupId === null ? t("roles.new") : t("roles.edit")}
            </h2>
          </div>
          <Button type="submit" disabled={save.isPending || name.trim() === "" || !dirty}>
            <ShieldCheck />{" "}
            {save.isPending
              ? t("common.saving")
              : groupId === null
                ? t("roles.createRole")
                : t("roles.saveRole")}
          </Button>
        </div>
        {/* Beside the button that caused it: an alert under a long grid is an
            alert nobody scrolls to, and the save looks as though it did nothing. */}
        {error ? (
          <p role="alert" className="text-danger text-callout mt-3">
            {error}
          </p>
        ) : null}
      </div>

      {/* Two sections: who the role is, then what it may do. They were one run
          of controls down the page, with the sentence explaining the table five
          elements above it. A name is a few words and a description is a
          sentence or two, so the description gets its own line. */}
      {/* The card runs the page's width so its edges meet the table below;
          the fields keep a readable measure and the count sits in the space
          that was otherwise left blank on the right. */}
      <section className="bg-surface-raised rounded-card mt-5 p-5 shadow-[var(--shadow-lift)]">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <h3 className="text-heading text-title-3">{t("roles.details")}</h3>
          {isSystem ? <Badge tone="neutral">{t("roles.builtIn")}</Badge> : null}
        </div>
        {/* The fields take the card's width. Held to a column they left the
            right half of a wide screen blank, and the description — which is
            the one field here that can run long — had the least room of all. */}
        <div className="grid gap-5">
          <Field
            label={t("roles.name")}
            htmlFor="role-name"
            hint={isSystem ? t("roles.builtInName") : undefined}
          >
            <Input
              id="role-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSystem}
              placeholder={t("roles.namePlaceholder")}
            />
          </Field>
          <Field label={t("roles.description")} htmlFor="role-description">
            <Textarea
              id="role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("roles.descriptionPlaceholder")}
            />
          </Field>
        </div>
      </section>

      <section className="bg-surface-raised rounded-card mt-4 shadow-[var(--shadow-lift)]">
        <div className="hairline-b p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h3 className="text-heading text-title-3">{t("roles.permissions")}</h3>
            <span className="text-fg-muted text-callout tabular-nums">
              {/* Both sides of the count measure the same thing. Counting
                  every ticked codename against the grantable ones read "136 of
                  126": the restricting marks are held like any other
                  permission and are not part of what there is to grant. */}
              {t("roles.count")
                .replace(
                  "{selected}",
                  String([...selected].filter((code) => grantable.has(code)).length),
                )
                .replace("{total}", String(all.length))}
            </span>
          </div>
          <p className="text-fg-muted text-callout mt-1">
            {holdsEverything ? t("roles.holdsEverything") : t("roles.tickIntro")}
          </p>
          {/* <p className="text-fg-muted text-footnote mt-1">{t("roles.sectionIntro")}</p> */}

          {/* Dashboard, Member Analytics and Maps administer nothing: they are
              views over records kept under other sections. Without this they
              are simply absent from the grid, and "how do I give someone the
              maps?" has no answer on the screen that is supposed to answer it. */}
          {BORROWED.length > 0 ? (
            <ul className="text-fg-muted text-footnote mt-3 grid gap-1">
              {BORROWED.map((area) => (
                <li key={area.key}>
                  <span className="text-fg font-medium">{t(area.label)}</span>
                  {" — "}
                  {t("roles.noRecords")} {t("roles.opens")}{" "}
                  <span className="tabular-nums">
                    {area.perms
                      .map((code) => labelForCodename(code, permissions, t))
                      .join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-fg-muted text-callout mr-1">{t("roles.quickStart")}</span>
            {/* Toggles, not one-way switches. They only ever added, so once a
                set was on the button did nothing and the only way back was
                Clear all — which reads as the button being broken. Pressed
                is the state, so it also says what is currently true. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={holdsEverything}
              aria-pressed={has(viewing)}
              className={cn(has(viewing) && "border-accent text-accent bg-accent-soft")}
              onClick={() => toggle(viewing, !has(viewing))}
            >
              {t("roles.seeEverything")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={holdsEverything}
              aria-pressed={has(all)}
              className={cn(has(all) && "border-accent text-accent bg-accent-soft")}
              onClick={() => toggle(all, !has(all))}
            >
              {t("roles.doEverything")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={holdsEverything}
              onClick={() => setSelected(new Set())}
            >
              {t("roles.clearAll")}
            </Button>
          </div>
        </div>

        {phone ? (
          /* One module at a time.

             The list says which modules there are and how much of each this
             role holds; opening one shows that module's records, each verb a
             row of its own. Nothing scrolls sideways, nothing is ticked against
             a column heading that has scrolled off the top, and the three
             levels the table stacks into one screen are three screens here —
             which is the only way they stop looking like each other. */
          <PhoneGrid
            sections={sections}
            rows={rows}
            opened={opened}
            setOpened={setOpened}
            tally={tally}
            disabled={holdsEverything}
            selected={selected}
            has={has}
            part={part}
            toggle={toggle}
            t={t}
          />
        ) : (
          /* No `overflow-x-auto` here, deliberately.
             `position: sticky` sticks to the nearest ancestor that scrolls, and
             a box with `overflow-x: auto` is one on both axes — CSS turns the
             other axis from `visible` into `auto` for you. So a wrapper here
             captured the header row and pinned it to the top of a box that
             never scrolls vertically, which looks exactly like sticky not
             working. Nothing is lost by dropping it: the shell's `<main>` is
             `overflow-y-auto`, and by the same rule it scrolls sideways too, so
             a grid wider than the screen still scrolls — in the one scroller
             the page already has, which is also the one the header now sticks
             to. Which is also why the narrow screen gets a list rather than
             this table with a scrollbar under it. */
          <div>
            <table className="text-callout w-full">
              <thead className="bg-surface-sunken text-fg text-subhead text-left">
                <tr>
                  {/* Sticky on the cells, not on `thead` or `tr`: a table row is
                      not a box that paints its own background, so a sticky row
                      over scrolling content shows the rows sliding through it.
                      Each cell carries the ground with it.

                      `--role-bar` is how far down the top is: the Save bar above
                      is sticky at 0, so a header pinned there too would slide
                      under it and disappear. One variable, set on the form, so
                      the two cannot drift. */}
                  <th className="bg-surface-sunken sticky top-[var(--role-bar)] z-10 px-4 py-3">
                    {t("roles.kindOfRecord")}
                  </th>
                  {VERBS.map((verb) => {
                    const column = rows
                      .map((r) => r.verbs[VERBS.indexOf(verb)]?.codename)
                      .filter((c): c is string => Boolean(c));
                    return (
                      <th
                        key={verb.action}
                        className="bg-surface-sunken sticky top-[var(--role-bar)] z-10 px-3 py-3 text-center"
                      >
                        <label className="inline-flex cursor-pointer flex-col items-center gap-1">
                          <span title={t(verb.hint)}>{t(verb.label)}</span>
                          <Checkbox
                            disabled={holdsEverything}
                            aria-label={t("roles.forEvery").replace("{verb}", t(verb.label))}
                            checked={has(column)}
                            ref={(box) => {
                              if (box) box.indeterminate = part(column);
                            }}
                            onChange={(e) => toggle(column, e.target.checked)}
                          />
                        </label>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {sections.map((section) => {
                const open = expanded.has(section.key);
                return (
                  <tbody key={section.key} className="divide-line divide-y">
                    {/* The heading row carries the same columns the rows below it
                        do, so "view everything about members" is one tick in the
                        View column of this row rather than fifty. */}
                    <tr className="bg-surface-sunken/60">
                      <th className="p-0 text-left font-normal">
                        <div className="flex items-center gap-3 px-4 py-2.5">
                          <button
                            type="button"
                            aria-expanded={open}
                            onClick={() =>
                              setExpanded((was) => {
                                const next = new Set(was);
                                if (!next.delete(section.key)) next.add(section.key);
                                return next;
                              })
                            }
                            className="text-fg text-callout inline-flex min-w-0 items-center gap-2 font-medium"
                          >
                            <ChevronRight
                              className={cn(
                                "size-4 shrink-0 transition-transform",
                                open && "rotate-90",
                              )}
                              aria-hidden
                            />
                            {t(section.label)}
                          </button>
                        </div>
                      </th>
                      {VERBS.map((verb, index) => {
                        // Only the rows that have this action: `Member` has no
                        // delete, so ticking Delete here must not claim it does.
                        const column = section.rows.flatMap((row) => row.columns[index] ?? []);
                        return (
                          <td key={verb.action} className="px-3 py-2.5 text-center">
                            {column.length ? (
                              <Checkbox
                                disabled={holdsEverything}
                                aria-label={`${t(verb.label)} — ${t(section.label)}`}
                                checked={has(column)}
                                ref={(box) => {
                                  if (box) box.indeterminate = part(column);
                                }}
                                onChange={(event) => toggle(column, event.target.checked)}
                              />
                            ) : (
                              <span className="text-fg-subtle">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {open
                      ? section.rows.map((row) => (
                          <tr
                            key={row.model}
                            className="hover:bg-surface-sunken transition-colors"
                          >
                            {/* Indented, and with a rule down the left: these
                                  belong to the heading above them, which a flat
                                  list gave no way to tell. */}
                            <td className="text-fg py-3 pr-4 pl-4 font-medium">
                              <span className="border-line ml-2 block border-l pl-5">
                                {row.label}
                                {row.options.length ? (
                                  <div className="mt-2 grid gap-1.5">
                                    {row.options.map((perm) => (
                                      <label
                                        key={perm.codename}
                                        className="text-fg-muted text-subhead inline-flex cursor-pointer items-center gap-2 font-normal"
                                      >
                                        <Checkbox
                                          disabled={holdsEverything}
                                          checked={selected.has(perm.codename)}
                                          onChange={(e) =>
                                            toggle([perm.codename], e.target.checked)
                                          }
                                        />
                                        {perm.name}
                                      </label>
                                    ))}
                                  </div>
                                ) : null}
                              </span>
                            </td>
                            {row.columns.map((column, index) => (
                              <td
                                key={VERBS[index]?.action}
                                className="px-3 py-3 text-center align-top"
                              >
                                {column.length ? (
                                  <Checkbox
                                    disabled={holdsEverything}
                                    // The verb's own word, not its translation
                                    // key: a screen reader was announcing this
                                    // box as "roles.add Members".
                                    aria-label={`${t(VERBS[index]!.label)} — ${row.label}`}
                                    checked={has(column)}
                                    // Part-ticked where the column stands for
                                    // several: two of the four enrolments is
                                    // neither on nor off, and a plain empty box
                                    // would read as none of them.
                                    ref={(box) => {
                                      if (box) box.indeterminate = part(column);
                                    }}
                                    onChange={(e) => toggle(column, e.target.checked)}
                                  />
                                ) : (
                                  <span className="text-fg-subtle">—</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))
                      : null}
                  </tbody>
                );
              })}
              {rows.length === 0 ? (
                <tbody>
                  <tr>
                    <td
                      colSpan={VERBS.length + 2}
                      className="text-fg-muted px-4 py-10 text-center"
                    >
                      {t("roles.noPermissions")}
                    </td>
                  </tr>
                </tbody>
              ) : null}
            </table>
          </div>
        )}
      </section>
    </Form>
  );
}
